import { type FC, type ReactNode, useEffect } from "react";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { getSubmissionTimeText, isSubmissionLate } from "components/materials/page/helpers";
import type { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox, type ListboxEntry, type ListboxOption } from "mds/components/Listbox";
import { PageIcon, VerifiedIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import {
  pageSelectors,
  selectSortedUserFullSubmissionsByPageId,
  submissionSelectors,
} from "store/selectors";
import type { FullGroupSubmissionType, FullIndividualSubmissionType } from "store/selectors/types";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { useQueryParam, useQueryParamsActions } from "utils/routing";

type SubmissionListboxOption = ListboxOption;

const canSeeAssessmentStatus = (canAuthorSection: boolean, assessmentsPublished: boolean) =>
  canAuthorSection || assessmentsPublished;

const getSubmissionSubtitle = (
  submission: Pick<
    FullIndividualSubmissionType | FullGroupSubmissionType,
    "created_at" | "assessment"
  >,
  page: Pick<PageType, "due_at" | "assessments_published_at"> | null,
  canAuthorSection: boolean,
  assessmentsPublished: boolean,
): ReactNode => {
  const timeText = getSubmissionTimeText(submission);
  const isLate = isSubmissionLate(submission, page?.due_at);
  const showAssessedText =
    canSeeAssessmentStatus(canAuthorSection, assessmentsPublished) && submission.assessment;

  return (
    <>
      <span className="text-black-tint-55">{timeText}</span>
      {showAssessedText ? (
        <>
          {" "}
          <span aria-hidden="true">&middot;</span>{" "}
          <span className="text-green-shade-20">{t("assessment_status_icon.assessed")}</span>
        </>
      ) : null}
      {isLate ? (
        <>
          {" "}
          <span aria-hidden="true">&middot;</span>{" "}
          <span className="text-orange-shade-20">{t("assessment_status_icon.late")}</span>
        </>
      ) : null}
    </>
  );
};

export type SubmissionMenuProps = {
  pageId: string;
  selectedAccessId?: string | null;
  showDraft?: boolean;
  isAssessing?: boolean;
};

/**
 * A dropdown menu that allows the user to select a submission to view. The menu assumes that there
 * is always at least one submission in the submissions list by the time the user is viewing this menu.
 * There are multiple ways to see this dropdown, 1) the student is on their My Submission tab, 2)
 * the instructor is in assessment mode, or 3) the instructor is on the Class Responses tab (showResponses) and
 * there are student submissions (where the dropdown menu will show both Draft and all the student's submissions).
 *
 * Seeing either the My Submission tab or accessing assessment mode are gated by whether there are any
 * submissions.
 *
 * By nature of how we collect submissions, there should also always be a submission for each student
 * by the time we reach this menu. There are two ways to collect submissions:
 * - the "Collect" button from the instructor, which will collect a submission per student in the course
 * - automatically collecting a submission per student on due date
 *
 * There should be no early submission for an individual student, and even if a student turns in a late
 * submission late, there should already be a default submission collected on due date. */
export const SubmissionMenu: FC<SubmissionMenuProps> = ({
  pageId,
  selectedAccessId,
  showDraft = false,
  isAssessing = false,
}) => {
  const { setQueryParams } = useQueryParamsActions();
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["assessments_published_at", "due_at"],
  );
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const currentSubmission = useObjectSelector(
    (s) => submissionSelectors.selectById(s, submissionId),
    ["id"],
  );
  const { canAuthorSection } = useCapabilities();
  const submissions = useListSelector(
    (s) => selectSortedUserFullSubmissionsByPageId(s, pageId, selectedAccessId),
    ["created_at", "official_at", "id", "assessment"],
  );

  const officialSubmission = submissions.find((s) => s.official_at);

  // If the user is assessing, the latest submission should be selected by default.
  //   NOTE: this code only applies for the first student being assessed. Subsequent
  //   students will have their default submission selected by AssessmentGradingView.
  // Otherwise:
  // If there's an official submission, that should be select by default.
  // If not, and there's at least one unofficial submission, the most recent
  //   unofficial submission should be selected by default.
  // Only if there are no submissions at all, should the draft be selected by default
  //   -- and then, only if we are showing the draft.
  let defaultMenuItem;
  if (isAssessing) {
    defaultMenuItem = submissions.length > 0 ? submissions[submissions.length - 1] : null;
  } else {
    defaultMenuItem =
      submissions.length > 0 ? officialSubmission || submissions[submissions.length - 1] : null; // null means draft
  }

  useEffect(() => {
    if (!submissionId && defaultMenuItem) {
      setQueryParams({ [SUBMISSION_ID_QUERY_PARAM]: defaultMenuItem.id });
    }
  }, [selectedAccessId, submissionId, defaultMenuItem, setQueryParams]);

  const assessmentsPublished = !!page?.assessments_published_at;

  if (submissions.length === 0 || (!currentSubmission && !showDraft)) {
    return null;
  }

  const selectedSubmissionIsDraft = submissionId === "draft";

  const items: Array<ListboxEntry<SubmissionListboxOption>> = [
    ...(showDraft
      ? [{ id: "draft", title: t("submission_menu.draft") } satisfies SubmissionListboxOption]
      : []),
    ...submissions.map((submission, index) => {
      const showVerifiedIcon =
        canSeeAssessmentStatus(canAuthorSection, assessmentsPublished) && !!submission.official_at;
      return {
        id: submission.id,
        title: t("submission_menu.submission_title", {
          number: index + 1,
          total: submissions.length,
        }),
        subtitle: getSubmissionSubtitle(submission, page, canAuthorSection, assessmentsPublished),
        iconStart: showVerifiedIcon ? <VerifiedIcon className="text-green" /> : <PageIcon />,
      } satisfies SubmissionListboxOption;
    }),
  ];

  return (
    <div className="flex min-w-0">
      <Listbox
        buttonSize="s"
        className="min-w-0"
        eventName="Submission selected"
        items={items}
        itemSize="xs"
        selectedValue={selectedSubmissionIsDraft ? "draft" : (submissionId ?? undefined)}
        onSelectionChange={(item) => {
          setQueryParams({ [SUBMISSION_ID_QUERY_PARAM]: item.id });
        }}
      />
    </div>
  );
};
